import { displayWithDecimals } from 'helpers/logica/display-with-decimals';
import { DetailedHTMLProps, FC, InputHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './CustomInputNumber.module.scss';

interface CustomInputNumberProps extends Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'onChange'> {
  precision?: number;
  onChange?: (value?: number) => void;
  min?: number;
  max?: number;
  step?: number;
  preserveOutOfRangeValue?: boolean;
}

export const CustomInputNumber: FC<CustomInputNumberProps> = ({ ...props }) => {
  const { value, onChange, precision = 0, onFocus, onBlur, min, max, step, disabled, style, preserveOutOfRangeValue = false, ...restProps } = props;
  const format = useCallback(value => displayWithDecimals(precision)(value), [precision]);
  const inputElRef = useRef<HTMLInputElement>(null);
  const [type, setType] = useState<'text' | 'number'>('text');
  const formattedValue = useMemo(() => (typeof value !== 'string' && typeof value !== 'number' ? value : format(parseFloat(value + ''))), [value, format]);
  const isOutOfRange = useMemo(
    () =>
      value !== undefined &&
      typeof value === 'number' &&
      ((min !== undefined && typeof min === 'number' && value < min) || (max !== undefined && typeof max === 'number' && value > max)),
    [value, min, max]
  );

  const [inputNumberValue, setInputNumberValue] = useState(value);

  useEffect(() => {
    if (document.activeElement !== inputElRef.current) {
      if (typeof value === 'number' && value.toString().includes('.') && value.toString().split('.')[1].length !== precision) {
        const newValue = value.toFixed(precision);
        const parsed = Number.parseFloat(newValue);
        setInputNumberValue(newValue);
        onChange?.(parsed);
        return;
      }
    }
    setInputNumberValue(value);
  }, [value, precision, onChange]);

  const customOnChange = useCallback(
    (event: { target: { value: string } }) => {
      const eventValue = event.target.value;
      if (onChange && type === 'number') {
        if (eventValue === '') {
          setInputNumberValue(eventValue);
        } else if (!eventValue.endsWith('.') && !eventValue.startsWith('.')) {
          const parsed = Number.parseFloat(eventValue);
          const isNumber = !isNaN(parsed);
          if (isNumber) {
            setInputNumberValue(parsed);
            onChange(parsed);
          }
        } else {
          setInputNumberValue(eventValue);
        }
      }
    },
    [onChange, type]
  );

  const customOnFocus = useCallback(
    event => {
      if (typeof value === 'number') {
        setInputNumberValue(value.toFixed(precision));
      }
      setType('number');
      onFocus?.(event);
    },
    [onFocus, value, precision]
  );

  const customOnBlur = useCallback(
    event => {
      let parsed;
      let isNumber = false;
      if (typeof inputNumberValue === 'string') {
        const replacedString = inputNumberValue.replace(',', '.');
        if (!replacedString.endsWith('.')) {
          parsed = Number.parseFloat(replacedString);
          isNumber = !isNaN(parsed);
        } else {
          return;
        }
      } else if (typeof inputNumberValue === 'number') {
        parsed = inputNumberValue;
        isNumber = true;
      }

      if (isNumber) {
        if (!preserveOutOfRangeValue && min !== undefined && typeof min === 'number' && parsed < min) {
          setInputNumberValue(min.toFixed(precision));
          onChange?.(Number.parseFloat(min.toFixed(precision)));
        } else if (!preserveOutOfRangeValue && max !== undefined && typeof max === 'number' && parsed > max) {
          onChange?.(Number.parseFloat(max.toFixed(precision)));
        } else {
          const fixed = parsed.toFixed(precision);
          const fixedNumber = Number.parseFloat(fixed);
          onChange?.(fixedNumber);
        }
      }

      setType('text');
      onBlur?.(event);
    },
    [onBlur, onChange, precision, inputNumberValue, min, max, preserveOutOfRangeValue]
  );

  const customOnKeyDown = useCallback(
    event => {
      const tmpStep = step && typeof step === 'number' ? step : 1;
      const currentValue = parseFloat(event.target.value);
      switch (event.key) {
        case 'ArrowUp':
          if (!isNaN(currentValue)) {
            const newValue = currentValue + tmpStep;
            if (max === undefined || newValue <= max) {
              setInputNumberValue(newValue.toFixed(precision));
            }
          }
          event.preventDefault();
          break;
        case 'ArrowDown':
          if (!isNaN(currentValue)) {
            const newValue = currentValue - tmpStep;
            if (min === undefined || newValue >= min) {
              setInputNumberValue(newValue.toFixed(precision)); // TODO: Zorgen dat dit niet meer afrond?
            }
          }
          event.preventDefault();
          break;
        case 'e':
        case 'Enter':
          event.preventDefault();
          break;
      }
    },
    [precision, step, max, min]
  );

  const customOnKeyUp = useCallback(
    event => {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        const currentValue = parseFloat(event.target.value);
        if (!isNaN(currentValue)) {
          onChange?.(currentValue);
        }
      }
      const code = event.keyCode ?? event.which;
      if (code === 9 && document.activeElement === event.target) {
        event.target.select();
      }
    },
    [onChange]
  );

  const customOnWheelCapture = useCallback(event => {
    // As event.preventDefault() doesn't work, we need to do the following to prevent the scroll from adding/subtracting
    if (event.target === document.activeElement) {
      event.target.blur();
      setTimeout(() => {
        event.target.focus();
      }, 1);
    }
  }, []);

  return (
    <div
      style={style}
      className={`ant-input-number ${styles.CustomInputNumber} ${
        type === 'number' ? 'ant-input-number-focused' : ''
      } ${isOutOfRange ? 'ant-input-number-out-of-range' : ''} ${disabled ? 'ant-input-number-disabled' : ''}`}>
      <div className="ant-input-number-input-wrap">
        <input
          {...restProps}
          ref={inputElRef}
          value={type === 'text' ? formattedValue : inputNumberValue}
          onFocus={customOnFocus}
          onChange={customOnChange}
          onBlur={customOnBlur}
          onKeyDown={customOnKeyDown}
          onKeyUp={customOnKeyUp}
          onWheelCapture={customOnWheelCapture}
          type={type}
          className="ant-input-number-input"
          lang="nl"
          min={min}
          max={max}
          step="any" // Disables standard validation based on step value. (We have custom steps)
          disabled={disabled}
        />
      </div>
    </div>
  );
};
